import { backendClient } from '../lib/api/backendClient';
import { getComposerName, registerComposerName } from '../utils/composerRegistry';
import { searchDebugLog, searchDebugWarn } from '../lib/debug/searchLogging';

export interface ComposerWork {
  id: string;
  title: string;
  composerId: string;
  composerName?: string;
  recordingCount?: number;
  catalogueNumber?: string;
  catalogueNumberNormalized?: string;
  catalogLabel?: string;
  summary?: string;
  imageUrl?: string;
  movementCount?: number;
  workId?: string;
}

type BackendComposerWork = Record<string, any>;

export interface ComposerWorksPagination {
  total: number;
  limit: number;
  offset: number;
}

export type FetchComposerWorksParams = {
  composerId: string;
  limit?: number;
  offset?: number;
};

export type FetchComposerWorksResult = {
  works: ComposerWork[];
  pagination: ComposerWorksPagination;
};

export type WorkLastPlayed = {
  workId: string;
  lastPlayedAt: string;
};

const DEFAULT_PAGE_SIZE = 50;
const WORK_CACHE_PREFIX = 'cmf.work.';
const WORK_CACHE_VERSION = 1;
const WORK_CACHE_TTL_MS = 14 * 24 * 60 * 60 * 1000;
const WORK_CACHE_JITTER_MS = 24 * 60 * 60 * 1000;
const WORK_CACHE_MAX_ENTRIES = 2000;
const canUseLocalStorage = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
const WORK_CACHE_LOG_PREFIX = '[workCache]';

const logWorkCache = (message: string, payload?: Record<string, unknown>) => {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log(WORK_CACHE_LOG_PREFIX, message, payload ?? {});
  }
};

const randomJitter = (): number => Math.floor(Math.random() * WORK_CACHE_JITTER_MS);

const makeCacheKey = (workId: string): string => `${WORK_CACHE_PREFIX}${workId}`;

const readCachedWork = (workId: string): ComposerWork | null | undefined => {
  if (!canUseLocalStorage) return undefined;
  try {
    const raw = localStorage.getItem(makeCacheKey(workId));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      localStorage.removeItem(makeCacheKey(workId));
      return undefined;
    }
    if (parsed.version !== WORK_CACHE_VERSION) {
      localStorage.removeItem(makeCacheKey(workId));
      return undefined;
    }
    const expiresAt = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0;
    if (!expiresAt || Date.now() > expiresAt) {
      localStorage.removeItem(makeCacheKey(workId));
      return undefined;
    }
    if (parsed.notFound) {
      return null;
    }
    return parsed.data as ComposerWork;
  } catch {
    localStorage.removeItem(makeCacheKey(workId));
    return undefined;
  }
};

const pruneWorkCacheIfNeeded = () => {
  if (!canUseLocalStorage) return;
  try {
    const keys: Array<{ key: string; expiresAt: number }> = [];
    const now = Date.now();
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(WORK_CACHE_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        const expiresAt = typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : 0;
        if (!expiresAt || expiresAt <= now || parsed?.version !== WORK_CACHE_VERSION) {
          localStorage.removeItem(key);
          continue;
        }
        keys.push({ key, expiresAt });
      } catch {
        localStorage.removeItem(key);
      }
    }

    if (keys.length > WORK_CACHE_MAX_ENTRIES) {
      const excess = keys.length - WORK_CACHE_MAX_ENTRIES;
      keys.sort((a, b) => a.expiresAt - b.expiresAt);
      for (let index = 0; index < excess; index += 1) {
        localStorage.removeItem(keys[index].key);
      }
    }
  } catch {
    // ignore cache cleanup errors
  }
};

const writeCachedWork = (workId: string, work: ComposerWork | null) => {
  if (!canUseLocalStorage) return;
  try {
    pruneWorkCacheIfNeeded();
    const payload = {
      version: WORK_CACHE_VERSION,
      expiresAt: Date.now() + WORK_CACHE_TTL_MS + randomJitter(),
      data: work,
      notFound: work === null,
    };
    localStorage.setItem(makeCacheKey(workId), JSON.stringify(payload));
  } catch {
    // ignore cache write errors
  }
};

const coerceString = (value: unknown): string | undefined => {
  if (typeof value === 'string' && value.trim().length) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
};

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim().length) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return undefined;
};

const resolveCatalogueNumber = (work: BackendComposerWork): string | undefined => {
  return (
    coerceString(work.catalogueNumber) ??
    coerceString(work.catalogNumber) ??
    coerceString(work.catalogDisplay) ??
    coerceString(work.catalog) ??
    undefined
  );
};

const resolveCatalogLabel = (work: BackendComposerWork): string | undefined => {
  const system = coerceString(work.system) ?? coerceString(work.catalogSystem) ?? coerceString(work.catalog);
  const number =
    coerceString(work.numberNormalized) ??
    coerceString(work.numberRaw) ??
    coerceString(work.baseNumber) ??
    coerceString(work.catalogueNumberNormalized);
  const display =
    coerceString(work.displayLabel) ??
    coerceString(work.display_label) ??
    coerceString(work.catalogDisplay) ??
    coerceString(work.catalog_display);

  if (display) {
    return display;
  }

  if (system && number) {
    return `${system} ${number}`.trim();
  }

  return system ?? number ?? undefined;
};

const resolveCatalogueNumberNormalized = (work: BackendComposerWork): string | undefined => {
  const nestedCatalog =
    typeof work.catalog === 'object' && work.catalog
      ? coerceString((work.catalog as Record<string, any>).normalized) ??
        coerceString((work.catalog as Record<string, any>).normalised) ??
        coerceString((work.catalog as Record<string, any>).numberNormalized) ??
        coerceString((work.catalog as Record<string, any>).number_normalized)
      : undefined;
  const nestedCatalogue =
    typeof work.catalogue === 'object' && work.catalogue
      ? coerceString((work.catalogue as Record<string, any>).normalized) ??
        coerceString((work.catalogue as Record<string, any>).normalised) ??
        coerceString((work.catalogue as Record<string, any>).numberNormalized) ??
        coerceString((work.catalogue as Record<string, any>).number_normalized)
      : undefined;

  return (
    coerceString(work.numberNormalized) ??
    coerceString(work.number_normalized) ??
    coerceString(work.catalogueNumberNormalized) ??
    coerceString(work.catalogue_number_normalized) ??
    coerceString(work.catalogNumberNormalized) ??
    coerceString(work.catalog_number_normalized) ??
    coerceString(work.catalogNormalized) ??
    coerceString(work.catalog_normalized) ??
    nestedCatalog ??
    nestedCatalogue ??
    undefined
  );
};

const resolveSummary = (work: BackendComposerWork): string | undefined => {
  const summary =
    work.summary ??
    work.description ??
    work.subtitle ??
    work.notes ??
    undefined;
  return typeof summary === 'string' ? summary : undefined;
};

const resolveImageUrl = (work: BackendComposerWork): string | undefined => {
  const candidates = [
    work.imageUrl,
    work.image_url,
    work.coverUrl,
    work.cover_url,
    work.posterUrl,
    work.poster_url,
    work.thumbnailUrl,
    work.thumbnail_url,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length) {
      return candidate.trim();
    }
  }

  return undefined;
};

const resolveComposerNameFromComposer = (composer: Record<string, any>): string | undefined => {
  return (
    coerceString(composer.name) ??
    coerceString(composer.displayName) ??
    coerceString(composer.display_name) ??
    coerceString(composer.fullName) ??
    coerceString(composer.full_name) ??
    coerceString(composer.sortableName) ??
    coerceString(composer.sortable_name) ??
    coerceString(composer.composerName) ??
    coerceString(composer.composer_name) ??
    coerceString(composer.primaryName) ??
    coerceString(composer.primary_name)
  );
};

const resolveComposerName = (work: BackendComposerWork): string | undefined => {
  const direct =
    coerceString(work.composerName) ??
    coerceString(work.composer_name) ??
    coerceString(work.composerDisplayName) ??
    coerceString(work.composer_display_name) ??
    coerceString(work.composerFullName) ??
    coerceString(work.composer_full_name) ??
    coerceString(work.composerLabel) ??
    coerceString(work.composer_label) ??
    coerceString(work.composer);

  if (direct) {
    return direct;
  }

  const composer = work.composer;
  if (composer && typeof composer === 'object') {
    const resolved = resolveComposerNameFromComposer(composer);
    if (resolved) {
      return resolved;
    }
  }

  const composers = work.composers;
  if (Array.isArray(composers)) {
    for (const entry of composers) {
      if (entry && typeof entry === 'object') {
        const resolved = resolveComposerNameFromComposer(entry);
        if (resolved) {
          return resolved;
        }
      } else {
        const resolved = coerceString(entry);
        if (resolved) return resolved;
      }
    }
  }

  return undefined;
};

const resolveMovementCount = (work: BackendComposerWork): number | undefined => {
  return (
    coerceNumber(work.movementCount) ??
    coerceNumber(work.movement_count) ??
    coerceNumber(work.movementsCount) ??
    coerceNumber(work.movements_count) ??
    coerceNumber(work.movementTotal) ??
    coerceNumber(work.movement_total) ??
    (Array.isArray(work.movements) ? work.movements.length : undefined)
  );
};

const pickPrimaryCatalog = (
  rows: BackendComposerWork[]
): BackendComposerWork | undefined => {
  if (!rows.length) return undefined;

  const preferred = rows.find(
    (row) =>
      row?.isDisplayPreferred === true ||
      String(row?.isDisplayPreferred).toLowerCase() === 'true'
  );
  if (preferred) return preferred;

  const byVariant = rows.find((row) => {
    const variant = coerceString(row.variantType) ?? coerceString(row.variant_type);
    return variant === 'base' || variant === 'primary';
  });
  if (byVariant) return byVariant;

  const bySystem = rows.find((row) => {
    const system = coerceString(row.system) ?? coerceString(row.catalog);
    const number =
      coerceString(row.numberNormalized) ??
      coerceString(row.numberRaw) ??
      coerceString(row.baseNumber);
    return Boolean(system && number);
  });
  if (bySystem) return bySystem;

  return rows[0];
};

const resolveCatalogTargetWorkId = (catalog: BackendComposerWork): string | undefined => {
  return (
    coerceString(catalog.workId) ??
    coerceString(catalog.work_id) ??
    coerceString((catalog as any)?.data?.workId) ??
    coerceString((catalog as any)?.data?.work_id) ??
    undefined
  );
};


const slugify = (value: string): string => {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const normalizeWork = (work: BackendComposerWork): ComposerWork => {
  const rawId = coerceString(work.id);
  const resolvedWorkId = coerceString(work.workId);
  const rowId = coerceString(work._meta?.rowId);
  const uuid =
    coerceString(work.uuid) ??
    coerceString(work.workUuid) ??
    coerceString(work.work_uuid) ??
    coerceString(work.workGuid) ??
    coerceString(work.work_guid) ??
    coerceString(work.guid);
  const slugCandidate =
    coerceString(work.slug) ??
    coerceString(work.workSlug) ??
    coerceString(work.work_slug) ??
    coerceString(work.slugifiedTitle) ??
    coerceString(work.slugified_title) ??
    coerceString(work.slugTitle) ??
    coerceString(work.slug_title);
  const legacyId =
    coerceString(work.legacyId) ??
    coerceString(work.legacy_id) ??
    coerceString(work.catalogWorkId) ??
    coerceString(work.catalog_work_id);

  const title =
    coerceString(work.title) ??
    coerceString(work.name) ??
    'Untitled Work';

  const composerId =
    coerceString(work.composerId) ??
    coerceString(work.composer_id) ??
    (typeof work.composer === 'object' ? coerceString(work.composer?.id) : undefined) ??
    coerceString(work.composerUuid) ??
    coerceString(work.composer_uuid) ??
    '';

  const titleSlug = slugCandidate ?? (title ? slugify(title) : undefined);
  const composerScopedSlug =
    titleSlug && composerId ? `${composerId}-${titleSlug}` : titleSlug;

  const identifier =
    resolvedWorkId ??
    rawId ??
    rowId ??
    uuid ??
    legacyId ??
    composerScopedSlug ??
    titleSlug ??
    '';

  const normalized: ComposerWork = {
    id: identifier,
    title,
    composerId,
  };

  const preferredWorkId =
    resolvedWorkId ??
    rawId ??
    rowId ??
    uuid ??
    legacyId ??
    composerScopedSlug ??
    titleSlug ??
    identifier;

  if (preferredWorkId) {
    normalized.workId = preferredWorkId;
  }

  if (!normalized.composerId) {
    const composer = work.composer;
    if (composer && typeof composer === 'object') {
      const nestedId =
        coerceString(composer.id) ??
        coerceString(composer.composerId) ??
        coerceString(composer.composer_id);
      if (nestedId) {
        normalized.composerId = nestedId;
      }
    }
  }

  const imageUrl = resolveImageUrl(work);
  if (imageUrl) {
    normalized.imageUrl = imageUrl;
  }

  const catalogLabel = resolveCatalogLabel(work);
  if (catalogLabel) {
    normalized.catalogLabel = catalogLabel;
    normalized.catalogueNumber = catalogLabel;
  }

  const catalogueNumber = resolveCatalogueNumber(work);
  if (catalogueNumber && !normalized.catalogueNumber) {
    normalized.catalogueNumber = catalogueNumber;
  }

  const catalogueNumberNormalized = resolveCatalogueNumberNormalized(work);
  if (catalogueNumberNormalized) {
    normalized.catalogueNumberNormalized = catalogueNumberNormalized;
  }

  const summary = resolveSummary(work);
  if (summary) {
    normalized.summary = summary;
  }

  const composerName = resolveComposerName(work);
  if (composerName) {
    normalized.composerName = composerName;
  }

  const movementCount = resolveMovementCount(work);
  if (typeof movementCount === 'number') {
    normalized.movementCount = movementCount;
  }

  const recordingCount =
    coerceNumber((work as BackendComposerWork).recordingCount) ??
    coerceNumber((work as BackendComposerWork).recording_count) ??
    coerceNumber((work as BackendComposerWork).recordingsCount) ??
    coerceNumber((work as BackendComposerWork).recordings_count) ??
    coerceNumber((work as BackendComposerWork).recordingTotal) ??
    coerceNumber((work as BackendComposerWork).recording_total);

  if (typeof recordingCount === 'number') {
    normalized.recordingCount = recordingCount;
  }

  return normalized;
};

const fetchComposerNameById = async (composerId: string): Promise<string | undefined> => {
  if (!composerId) {
    return undefined;
  }

  try {
    const { data } = await backendClient.get<{ composer?: Record<string, any> }>(
      `/catalog/composers/${encodeURIComponent(composerId)}`
    );
    const composer = data?.composer;
    if (composer && typeof composer === 'object') {
      return (
        resolveComposerNameFromComposer(composer) ??
        coerceString(composer.displayLabel) ??
        coerceString(composer.display_label)
      );
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[worksService] Failed to fetch composer by ID', composerId, error);
    }
  }

  return undefined;
};

const fetchComposerCatalogs = async (
  composerId: string,
  workId?: string
): Promise<BackendComposerWork[]> => {
  if (!composerId) {
    return [];
  }

  try {
    const { data } = await backendClient.get<{ catalogs?: BackendComposerWork[] }>(
      `/catalog/composers/${encodeURIComponent(composerId)}/catalogs`,
      workId ? { params: { workId } } : undefined
    );
    return Array.isArray(data?.catalogs) ? data.catalogs : [];
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[worksService] fetchComposerCatalogs failed', { composerId, error });
    }
    return [];
  }
};

const sanitizeLimit = (value?: number): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  const floored = Math.floor(value);
  if (floored <= 0) {
    return undefined;
  }
  return floored;
};

const sanitizeOffset = (value?: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  const floored = Math.floor(value);
  return floored >= 0 ? floored : 0;
};

const buildPagination = (
  raw: Record<string, any> | undefined,
  fallback: { total: number; limit: number; offset: number }
): ComposerWorksPagination => {
  const rawTotal = coerceNumber(raw?.total);
  const rawLimit = coerceNumber(raw?.limit);
  const rawOffset = coerceNumber(raw?.offset);

  const offset = typeof rawOffset === 'number' && rawOffset >= 0 ? rawOffset : fallback.offset;
  const limit =
    typeof rawLimit === 'number' && rawLimit > 0 ? rawLimit : fallback.limit > 0 ? fallback.limit : DEFAULT_PAGE_SIZE;
  const minimumTotal = Math.max(offset + fallback.total, 0);
  const total =
    typeof rawTotal === 'number' && rawTotal >= 0
      ? rawTotal
      : Math.max(minimumTotal, fallback.total, 0);

  return { total, limit, offset };
};

export const worksService = {
  async fetchComposerWorks(params: FetchComposerWorksParams): Promise<FetchComposerWorksResult> {
    const composerId = params?.composerId?.trim?.() ?? params?.composerId;
    const resolvedComposerId = typeof composerId === 'string' ? composerId : '';
    const requestedLimit = sanitizeLimit(params?.limit);
    const requestedOffset = sanitizeOffset(params?.offset);

    if (!resolvedComposerId) {
      return {
        works: [],
        pagination: {
          total: 0,
          limit: requestedLimit ?? DEFAULT_PAGE_SIZE,
          offset: requestedOffset,
        },
      };
    }

    try {
      const queryParams: Record<string, any> = { composerId: resolvedComposerId };
      if (typeof requestedLimit === 'number') {
        queryParams.limit = requestedLimit;
      }
      if (typeof requestedOffset === 'number' && requestedOffset > 0) {
        queryParams.offset = requestedOffset;
      }

      const { data } = await backendClient.get<{
        works?: BackendComposerWork[];
        pagination?: Record<string, any>;
      }>(
        '/catalog/works',
        { params: queryParams }
      );

      const works = Array.isArray(data?.works) ? data.works : [];
      const normalizedWorks = works.map(normalizeWork);

      const pagination = buildPagination(data?.pagination, {
        total: normalizedWorks.length,
        limit: requestedLimit ?? (normalizedWorks.length || DEFAULT_PAGE_SIZE),
        offset: requestedOffset,
      });

      return { works: normalizedWorks, pagination };
    } catch (error: any) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[worksService] fetchComposerWorks failed', error);
      }
      if (error?.response?.status === 404) {
        return {
          works: [],
          pagination: {
            total: 0,
            limit: requestedLimit ?? DEFAULT_PAGE_SIZE,
            offset: requestedOffset,
          },
        };
      }
      throw error;
    }
  },

  async fetchWorksLastPlayed(workIds: string[]): Promise<Map<string, number>> {
    const result = new Map<string, number>();
    if (!Array.isArray(workIds) || workIds.length === 0) {
      return result;
    }

    const uniqueIds = Array.from(
      new Set(
        workIds
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id) => id.length > 0)
      )
    ).slice(0, 3000);

    if (!uniqueIds.length) {
      return result;
    }

    const parseTimestamp = (value: unknown): number | null => {
      if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isFinite(parsed) ? parsed : null;
      }
      if (value instanceof Date) {
        const ms = value.valueOf();
        return Number.isFinite(ms) ? ms : null;
      }
      return null;
    };

    try {
      const { data } = await backendClient.post<{ items?: WorkLastPlayed[] }>(
        '/library/works/last-played',
        { workIds: uniqueIds }
      );
      const items = Array.isArray(data?.items) ? data.items : [];
      for (const entry of items) {
        const workId = typeof entry?.workId === 'string' ? entry.workId.trim() : undefined;
        const ts = parseTimestamp(entry?.lastPlayedAt);
        if (workId && ts !== null) {
          result.set(workId, ts);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[worksService] fetchWorksLastPlayed failed', error);
      }
    }

    return result;
  },

  async searchWorks(query: string, limit = 10): Promise<ComposerWork[]> {
    const trimmedQuery = typeof query === 'string' ? query.trim() : '';
    if (!trimmedQuery) {
      searchDebugLog('worksService.searchWorks.skip_empty', { rawQuery: query });
      return [];
    }

    try {
      const params: Record<string, any> = { q: trimmedQuery };
      if (typeof limit === 'number' && Number.isFinite(limit)) {
        params.limit = limit;
      }

      searchDebugLog('worksService.searchWorks.request', {
        query: trimmedQuery,
        limit: params.limit,
      });

      const { data } = await backendClient.get<{ works?: BackendComposerWork[] }>(
        '/catalog/works/search',
        { params }
      );

      const works = Array.isArray(data?.works) ? data.works : [];
      const normalized = works.map(normalizeWork);

      normalized.forEach((work) => {
        if (!work.composerId) return;
        if (work.composerName) {
          registerComposerName(work.composerId, work.composerName);
          return;
        }
        const cachedName = getComposerName(work.composerId);
        if (cachedName) {
          work.composerName = cachedName;
        }
      });

      const missingComposerIds = Array.from(
        new Set(
          normalized
            .filter((work) => !work.composerName && work.composerId)
            .map((work) => work.composerId as string)
        )
      );

      if (missingComposerIds.length) {
        const composerLookups = await Promise.allSettled(
          missingComposerIds.map((composerId) => fetchComposerNameById(composerId))
        );

        const composerNames = new Map<string, string>();
        composerLookups.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            composerNames.set(missingComposerIds[index], result.value);
          }
        });

        if (composerNames.size) {
          normalized.forEach((work) => {
            if (!work.composerName && work.composerId) {
              const resolved = composerNames.get(work.composerId);
              if (resolved) {
                work.composerName = resolved;
                registerComposerName(work.composerId, resolved);
              }
            }
          });
        }
      }

      searchDebugLog('worksService.searchWorks.success', {
        query: trimmedQuery,
        returned: works.length,
        normalized: normalized.length,
        missingComposerIds: missingComposerIds.length,
      });

      return normalized;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        searchDebugLog('worksService.searchWorks.404', { query: trimmedQuery });
        return [];
      }

      searchDebugWarn('worksService.searchWorks.error', {
        query: trimmedQuery,
        status,
        message: error?.message,
      });
      throw error;
    }
  },

  async fetchWorksByIds(workIds: string[]): Promise<ComposerWork[]> {
    if (!Array.isArray(workIds) || workIds.length === 0) {
      return [];
    }

    const normalizedIds = Array.from(
      new Set(
        workIds
          .map((value) => (typeof value === 'string' ? value.trim() : ''))
          .filter((value): value is string => value.length > 0)
      )
    );

    if (!normalizedIds.length) {
      return [];
    }

    const MAX_BATCH_SIZE = 500;
    const batches: string[][] = [];
    for (let index = 0; index < normalizedIds.length; index += MAX_BATCH_SIZE) {
      batches.push(normalizedIds.slice(index, index + MAX_BATCH_SIZE));
    }

    const results: ComposerWork[] = [];
    const missingIds: string[] = [];
    let hitCount = 0;

    normalizedIds.forEach((id) => {
      const cached = readCachedWork(id);
      if (cached === undefined) {
        missingIds.push(id);
      } else if (cached !== null) {
        results.push(cached);
        hitCount += 1;
      }
      // cached null => known miss, skip network
    });

    if (!missingIds.length) {
      logWorkCache('all hits; no fetch needed', { total: normalizedIds.length, hits: hitCount });
      return results;
    }

    logWorkCache('cache miss fetch start', {
      total: normalizedIds.length,
      hits: hitCount,
      missing: missingIds.length,
    });

    if (!missingIds.length) {
      return results;
    }

    for (const batch of batches) {
      const pendingBatchIds = batch.filter((id) => missingIds.includes(id));
      if (!pendingBatchIds.length) continue;
      try {
        logWorkCache('requesting batch', { size: pendingBatchIds.length, batches: batches.length });
        const { data } = await backendClient.post<{ works?: BackendComposerWork[] }>(
          '/catalog/works/batch',
          { workIds: pendingBatchIds }
        );
        const works = Array.isArray(data?.works) ? data.works : [];
        const normalizedWorks = works.map(normalizeWork);
        results.push(...normalizedWorks);
        normalizedWorks.forEach((work) => {
          const id = work.id ?? work.workId;
          if (id) {
            writeCachedWork(id, work);
          }
        });

        const returnedIds = new Set(
          normalizedWorks
            .map((work) => work.id ?? work.workId)
            .filter((id): id is string => typeof id === 'string' && id.length > 0)
        );
        pendingBatchIds.forEach((id) => {
          if (!returnedIds.has(id)) {
            writeCachedWork(id, null);
          }
        });
        logWorkCache('batch complete', {
          fetched: normalizedWorks.length,
          unresolved: pendingBatchIds.length - normalizedWorks.length,
        });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[worksService] fetchWorksByIds failed', { batch, error });
        }
      }
    }

    const unresolved = missingIds.filter((id) => !results.find((w) => w.id === id || w.workId === id));
    logWorkCache('cache fetch complete', {
      total: normalizedIds.length,
      hits: hitCount,
      fetched: results.length - hitCount,
      unresolved: unresolved.length,
    });
    return results;
  },

  async fetchWorkById(workId: string): Promise<ComposerWork | null> {
    if (!workId) {
      return null;
    }

    try {
      const { data } = await backendClient.get<{ work?: BackendComposerWork }>(
        `/catalog/works/${encodeURIComponent(workId)}`
      );

      if (!data?.work) {
        return null;
      }

      const normalized = normalizeWork(data.work);

      if (normalized.composerId && normalized.composerName) {
        registerComposerName(normalized.composerId, normalized.composerName);
      }

      if (!normalized.composerName && normalized.composerId) {
        const cachedName = getComposerName(normalized.composerId);
        if (cachedName) {
          normalized.composerName = cachedName;
        } else {
          try {
            const composerName = await fetchComposerNameById(normalized.composerId);
            if (composerName) {
              normalized.composerName = composerName;
              registerComposerName(normalized.composerId, composerName);
            }
          } catch (composerError) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[worksService] Failed to enrich composer name', normalized.composerId, composerError);
            }
          }
        }
      }

      const lookupWorkId = normalized.id ?? normalized.workId ?? workId;
      const catalogs =
        normalized.composerId && lookupWorkId
          ? await fetchComposerCatalogs(normalized.composerId, lookupWorkId)
          : [];

      if (catalogs.length) {
        const workIdCandidates = new Set<string>();
        if (normalized.workId) workIdCandidates.add(normalized.workId);
        if (normalized.id) workIdCandidates.add(normalized.id);
        if (workId) workIdCandidates.add(workId);

        const workCatalogs = catalogs.filter((catalog) => {
          const target = resolveCatalogTargetWorkId(catalog);
          return target ? workIdCandidates.has(target) : false;
        });

        const primaryCatalog = pickPrimaryCatalog(workCatalogs);

        if (primaryCatalog) {
          const label = resolveCatalogLabel(primaryCatalog);
          const normalizedNumber =
            coerceString(primaryCatalog.numberNormalized) ??
            coerceString(primaryCatalog.numberRaw) ??
            coerceString(primaryCatalog.baseNumber);

          if (label) {
            normalized.catalogueNumber = label;
            normalized.catalogLabel = label;
          }

          if (normalizedNumber) {
            normalized.catalogueNumberNormalized = normalizedNumber;
          }
        }
      }

      return normalized;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        return null;
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn('[worksService] fetchWorkById failed', error);
      }
      throw error;
    }
  },

  async fetchRecordingCounts(workIds: string[]): Promise<Record<string, number>> {
    if (!Array.isArray(workIds) || workIds.length === 0) {
      return {};
    }

    const normalized = Array.from(
      new Set(
        workIds
          .map((id) => (typeof id === 'string' ? id.trim() : ''))
          .filter((id): id is string => id.length > 0)
      )
    );

    if (!normalized.length) {
      return {};
    }

    const MAX_BATCH_SIZE = 500;
    const batches: string[][] = [];
    for (let index = 0; index < normalized.length; index += MAX_BATCH_SIZE) {
      batches.push(normalized.slice(index, index + MAX_BATCH_SIZE));
    }

    try {
      const result: Record<string, number> = {};

      for (const batch of batches) {
        const { data } = await backendClient.post<{ counts?: Record<string, unknown> }>(
          '/catalog/works/recording-counts',
          { workIds: batch }
        );

        const incoming = data?.counts;
        if (!incoming || typeof incoming !== 'object') {
          continue;
        }

        Object.entries(incoming).forEach(([key, value]) => {
          const numeric = coerceNumber(value);
          if (typeof numeric === 'number') {
            result[key] = numeric;
          }
        });
      }

      return result;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[worksService] fetchRecordingCounts failed', { workIds: normalized, error });
      }
      throw error;
    }
  },
};

export type { ComposerWork as CatalogComposerWork };
